
import { Invoice } from '@/types/payments';
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { userFacingErrorMessage } from '@/utils/userFacingError';

export function useInvoicePaymentProcessing(
  invoices: Invoice[],
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>,
  setPaymentDialogOpen: (open: boolean) => void,
  setCancelDialogOpen: (open: boolean) => void,
  actionInvoiceId: string | null,
  selectedInvoices: string[]
) {
  const t = useTranslations('consultantToasts');

  const processPayment = async () => {
    if (actionInvoiceId) {
      try {
        // Simulate payment processing
        console.log(`Processing payment for invoice: ${actionInvoiceId}`);
        setInvoices((prevInvoices) =>
          prevInvoices.map((invoice) =>
            invoice.id === actionInvoiceId ? { ...invoice, status: 'pending' } : invoice
          )
        );
        toast(t('paymentProcessing.initiatedTitle'), {
          description: t('paymentProcessing.initiatedBody', { id: actionInvoiceId }),
        });
      } catch (error: any) {
        console.error("Payment processing failed:", error);
        toast.error(t('common.error'), {
          description: t('paymentProcessing.failedBody', {
            id: actionInvoiceId,
            reason: userFacingErrorMessage(error, t('paymentProcessing.tryAgain')),
          }),
        });
      } finally {
        setPaymentDialogOpen(false);
      }
    }
  };

  const processCancellation = async () => {
    if (actionInvoiceId) {
      try {
        // Simulate cancellation processing
        console.log(`Processing cancellation for invoice: ${actionInvoiceId}`);
        setInvoices((prevInvoices) =>
          prevInvoices.map((invoice) =>
            invoice.id === actionInvoiceId ? { ...invoice, status: 'unpaid' } : invoice
          )
        );
        toast(t('paymentProcessing.cancellationTitle'), {
          description: t('paymentProcessing.cancellationBody', { id: actionInvoiceId }),
        });
      } catch (error: any) {
        console.error("Cancellation processing failed:", error);
        toast.error(t('common.error'), {
          description: t('paymentProcessing.cancellationFailedBody', {
            id: actionInvoiceId,
            reason: userFacingErrorMessage(error, t('paymentProcessing.tryAgain')),
          }),
        });
      } finally {
        setCancelDialogOpen(false);
      }
    }
  };

  return {
    processPayment,
    processCancellation
  };
}
